import { useCallback, useEffect, useMemo, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { validateSOPSubmission } from '@/features/offers/helpers/sop.helper'
import { OfferStatus } from '@/features/offers/constants'

import {
  Button,
  Flex,
  HStack,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
  Box,
} from '@chakra-ui/react'

import { Radio } from '@/components/Form'
import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'
import { selectOfferDetail } from '@/features/offers/slices/offerDetail.slice'
import { SOPQuestionnaireModalSelector, hideSOPQuestionnaireModal } from '@/features/offers/slices/offerModal.slice'
import useOffer from '@/features/offers/hooks/useOffer'
import { formatOffer } from '@/features/offers/helpers/formatOffer'

const SOPQuestionnaireModal = () => {
  const { show } = useSelector(SOPQuestionnaireModalSelector)
  const { adminMode } = useSelector(selectOfferDetail)
  const dispatch = useDispatch()

  const { useUpdateOffer } = useOffer()
  const updateOffer = useUpdateOffer()

  const [totalPrice, setTotalPrice] = useState(0)
  const [isContractValueOver5Million, setIsContractValueOver5Million] = useState(false)

  const { setValue, getValues, watch } = useFormContext()
  const [fieldResetKeys, setFieldResetKeys] = useState({})

  const subSystems = useWatch({ name: 'subSystems' })
  const countryOfInstallation = useWatch({ name: 'countryOfInstallation' })
  const siemensRoleInTheOffer = useWatch({ name: 'siemensRoleInTheOffer' })
  const offerStatus = useWatch({ name: 'status' })
  // Allow editing SOP at Quotation Draft and Pending Contributor Approval (QUOTATION_SUBMITTED)
  const isSopEditable = [OfferStatus.QUOTATION_DRAFT, OfferStatus.QUOTATION_SUBMITTED].includes(offerStatus)
  // Keep backward-compat variable name for footer buttons
  const isOfferInBookingStatus = !isSopEditable

  const watchedSOPQuestionAnswers = useWatch({ name: 'SOPQuestionAnswers' })
  const SOPQuestionAnswers = useMemo(() => watchedSOPQuestionAnswers || {}, [watchedSOPQuestionAnswers])
  const offerId = useWatch({ name: '_id' })

  const resetAnswer = useCallback(
    (fieldName) => {
      setValue(fieldName, undefined, { shouldDirty: true })
      setFieldResetKeys((prev) => ({
        ...prev,
        [fieldName]: (prev[fieldName] || 0) + 1,
      }))
    },
    [setValue]
  )

  useEffect(() => {
    if (countryOfInstallation === 'hk') {
      setValue('SOPQuestionAnswers.isCountryOfInstallHK', true, { shouldDirty: true })
      if (siemensRoleInTheOffer === 'mainContract') {
        setValue('SOPQuestionAnswers.isMainContractSignedAfterAug2025', true, { shouldDirty: true })
        if (isContractValueOver5Million) {
          setValue('SOPQuestionAnswers.isOver5Million', true, { shouldDirty: true })
        } else {
          setValue('SOPQuestionAnswers.isOver5Million', false, { shouldDirty: true })
        }
      } else if (siemensRoleInTheOffer && siemensRoleInTheOffer !== 'mainContract') {
        setValue('SOPQuestionAnswers.isMainContractSignedAfterAug2025', false, { shouldDirty: true })
      }
    } else if (countryOfInstallation && countryOfInstallation !== '') {
      setValue('SOPQuestionAnswers.isCountryOfInstallHK', false, { shouldDirty: true })
    }
  }, [countryOfInstallation, siemensRoleInTheOffer, isContractValueOver5Million, setValue])

  useEffect(() => {
    if (subSystems) {
      const totalPrice = subSystems.reduce((systemPrice, subSystem) => {
        if (subSystem.costSheetContractValue !== undefined && subSystem.costSheetContractValue !== null) {
          return systemPrice + subSystem.costSheetContractValue
        }

        const subSystemTotal =
          subSystem.totalContractPrice?.newContractRows?.reduce((sum, row) => {
            return sum + row.totalHKD
          }, 0) || 0

        return systemPrice + subSystemTotal
      }, 0)

      setTotalPrice(totalPrice)
      if (totalPrice >= 5000000) {
        setIsContractValueOver5Million(true)
      } else {
        setIsContractValueOver5Million(false)
      }
    }
  }, [subSystems])

  useEffect(() => {
    if (show && countryOfInstallation) {
      if (countryOfInstallation === 'hk') {
        setValue('SOPQuestionAnswers.isCountryOfInstallHK', true, { shouldDirty: false })
      } else if (countryOfInstallation !== '') {
        setValue('SOPQuestionAnswers.isCountryOfInstallHK', false, { shouldDirty: false })
      }
    }
  }, [show, countryOfInstallation, setValue])

  // Visibility and enabled states for questions
  const questionStates = useMemo(() => {
    const answers = SOPQuestionAnswers || {}
    const states = {
      Q0: { visible: true, enabled: true },
      Q1: { visible: false, enabled: false },
      Q2: { visible: false, enabled: false },
      Q3: { visible: false, enabled: false },
      Q4: { visible: false, enabled: false },
      Q5: { visible: false, enabled: false },
      Q6: { visible: false, enabled: false },
    }

    // Q1 is visible when Q0 = Yes (HK)
    if (answers.isCountryOfInstallHK === true) {
      states.Q1.visible = true
      states.Q1.enabled = true
    }

    // Q2 is visible when Q1 = Yes
    if (answers.isMainContractSignedAfterAug2025 === true) {
      states.Q2.visible = true
      states.Q2.enabled = true
    }

    // When Q2 = Yes, Q3 is visible
    if (answers.isOver5Million === true) {
      states.Q3.visible = true
      states.Q3.enabled = true

      // Q4 is visible only when Q2 = Yes AND Q3 = No (not when Q3 = Yes)
      if (answers.isPublicContract === false) {
        states.Q4.visible = true
        states.Q4.enabled = true

        // Q5 is visible when Q2 = Yes and Q3 = No and Q4 = No
        if (answers.isNewBuildingProject === false) {
          states.Q5.visible = true
          states.Q5.enabled = true

          // Q6 is visible when Q2 = Yes, Q3 = No, Q4 = No, Q5 = Yes
          if (answers.isValueOfWorkRequiredBAApproval === true) {
            states.Q6.visible = true
            states.Q6.enabled = true
          }
        }
      }
    }

    return states
  }, [SOPQuestionAnswers])

  // Initialize default values and reset dependent questions
  useEffect(() => {
    if (!show) return

    const answers = SOPQuestionAnswers || {}

    // Reset Q1 and subsequent questions if Q0 = No
    if (answers.isCountryOfInstallHK === false) {
      if (answers.isMainContractSignedAfterAug2025 !== undefined) {
        resetAnswer('SOPQuestionAnswers.isMainContractSignedAfterAug2025')
      }
      if (answers.isOver5Million !== undefined) {
        resetAnswer('SOPQuestionAnswers.isOver5Million')
      }
      if (answers.isPublicContract !== undefined) {
        resetAnswer('SOPQuestionAnswers.isPublicContract')
      }
      if (answers.isNewBuildingProject !== undefined) {
        resetAnswer('SOPQuestionAnswers.isNewBuildingProject')
      }
      if (answers.isValueOfWorkRequiredBAApproval !== undefined) {
        resetAnswer('SOPQuestionAnswers.isValueOfWorkRequiredBAApproval')
      }
      if (answers.isNonResidentialOver5Million !== undefined) {
        resetAnswer('SOPQuestionAnswers.isNonResidentialOver5Million')
      }
    }

    // Reset Q2 and subsequent questions if Q1 = No
    if (answers.isMainContractSignedAfterAug2025 === false) {
      if (answers.isOver5Million !== undefined) {
        resetAnswer('SOPQuestionAnswers.isOver5Million')
      }
      if (answers.isPublicContract !== undefined) {
        resetAnswer('SOPQuestionAnswers.isPublicContract')
      }
      if (answers.isNewBuildingProject !== undefined) {
        resetAnswer('SOPQuestionAnswers.isNewBuildingProject')
      }
      if (answers.isValueOfWorkRequiredBAApproval !== undefined) {
        resetAnswer('SOPQuestionAnswers.isValueOfWorkRequiredBAApproval')
      }
      if (answers.isNonResidentialOver5Million !== undefined) {
        resetAnswer('SOPQuestionAnswers.isNonResidentialOver5Million')
      }
    }

    // Note: Q3, Q4, and Q5 are NOT auto-initialized to prevent them from showing simultaneously
    // They will only be visible when the previous question is explicitly answered, ensuring one-by-one display
    // Q3 becomes visible when Q2 = Yes (handled by questionStates visibility logic)
    // Q4 becomes visible when Q3 = No (handled by questionStates visibility logic)
    // Q5 becomes visible when Q4 = No (handled by questionStates visibility logic)

    // Initialize Q6 default value only when Q5 = Yes
    if (
      answers.isOver5Million === true &&
      answers.isPublicContract === false &&
      answers.isNewBuildingProject === false &&
      answers.isValueOfWorkRequiredBAApproval === true
    ) {
      if (answers.isNonResidentialOver5Million === undefined) {
        setValue('SOPQuestionAnswers.isNonResidentialOver5Million', false, { shouldDirty: false })
      }
    }

    // Reset Q3 and subsequent questions if Q2 = No
    if (answers.isOver5Million === false) {
      if (answers.isPublicContract !== undefined) {
        resetAnswer('SOPQuestionAnswers.isPublicContract')
      }
      if (answers.isNewBuildingProject !== undefined) {
        resetAnswer('SOPQuestionAnswers.isNewBuildingProject')
      }
      if (answers.isValueOfWorkRequiredBAApproval !== undefined) {
        resetAnswer('SOPQuestionAnswers.isValueOfWorkRequiredBAApproval')
      }
      if (answers.isNonResidentialOver5Million !== undefined) {
        resetAnswer('SOPQuestionAnswers.isNonResidentialOver5Million')
      }
    }

    // Reset Q4 and subsequent questions if Q3 = Yes
    if (answers.isPublicContract === true) {
      if (answers.isNewBuildingProject !== undefined) {
        resetAnswer('SOPQuestionAnswers.isNewBuildingProject')
      }
      if (answers.isValueOfWorkRequiredBAApproval !== undefined) {
        resetAnswer('SOPQuestionAnswers.isValueOfWorkRequiredBAApproval')
      }
      if (answers.isNonResidentialOver5Million !== undefined) {
        resetAnswer('SOPQuestionAnswers.isNonResidentialOver5Million')
      }
    }

    // Reset Q5 and subsequent questions if Q4 = Yes
    if (answers.isNewBuildingProject === true) {
      if (answers.isValueOfWorkRequiredBAApproval !== undefined) {
        resetAnswer('SOPQuestionAnswers.isValueOfWorkRequiredBAApproval')
      }
      if (answers.isNonResidentialOver5Million !== undefined) {
        resetAnswer('SOPQuestionAnswers.isNonResidentialOver5Million')
      }
    }

    // Reset Q6 if Q5 = No
    if (answers.isValueOfWorkRequiredBAApproval === false) {
      if (answers.isNonResidentialOver5Million !== undefined) {
        resetAnswer('SOPQuestionAnswers.isNonResidentialOver5Million')
      }
    }
  }, [SOPQuestionAnswers, show, setValue, resetAnswer])

  const handleModalClose = () => {
    dispatch(hideSOPQuestionnaireModal())
  }

  const onSubmit = async () => {
    if (!offerId) {
      toast.error('Offer not found')
      return
    }

    try {
      dispatch(showLoadingOverlay())

      const currentOffer = getValues()
      const formattedOffer = formatOffer(currentOffer)

      validateSOPSubmission({
        countryOfInstallation: formattedOffer.countryOfInstallation,
        isEQuotation: formattedOffer.isEQuotation,
        offerCreateDate: formattedOffer.offerCreateDate,
        SOPQuestionAnswers: formattedOffer.SOPQuestionAnswers,
      })

      await updateOffer.mutateAsync({
        id: offerId,
        data: formattedOffer,
      })

      toast.success('SOP Questionnaire saved successfully')
      handleModalClose()
    } catch (error) {
      console.error('Failed to save SOP questionnaire:', error)
      toast.error('Failed to save SOP questionnaire. Please try again.')
    } finally {
      dispatch(hideLoadingOverlay())
    }
  }

  return (
    <Modal isOpen={show} onClose={handleModalClose} size="xl" isCentered>
      <ModalOverlay />
      <ModalContent maxH="90vh" display="flex" flexDirection="column" overflow="hidden">
        <form
          onSubmit={(e) => {
            e.preventDefault()
            onSubmit()
          }}
          style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}
        >
          <ModalHeader flexShrink={0}>Security Of Payment Questionnaire</ModalHeader>
          <ModalCloseButton />
          <ModalBody
            overflowY="auto"
            flex="1"
            minH={0}
            px={6}
            sx={{
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'gray.100',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'gray.400',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: 'gray.500',
              },
            }}
          >
            <VStack spacing={4} align="stretch">
              <Box pb={3} borderBottom="1px solid" borderColor="gray.100">
                <Text fontWeight="semibold" mb={3} fontSize="md">
                  Q0: Is the country of installation in HK?
                </Text>
                <Radio
                  name="SOPQuestionAnswers.isCountryOfInstallHK"
                  options={[
                    { value: true, label: 'Yes' },
                    { value: false, label: 'No' },
                  ]}
                  isDisabled={true}
                  isRow
                  defaultValue={false}
                />
                <Text fontSize="xs" color="black.500" mt={2} fontStyle="italic">
                  (Auto-filled from offer&apos;s country of installation)
                </Text>
              </Box>

              {questionStates.Q1.visible && (
                <Box pb={3} borderBottom="1px solid" borderColor="gray.100">
                  <Text fontWeight="semibold" mb={3} fontSize="md">
                    Q1: Is main contract signed after 28/08/2025?
                  </Text>
                  <Radio
                    key={`SOPQuestionAnswers.isMainContractSignedAfterAug2025-${
                      fieldResetKeys['SOPQuestionAnswers.isMainContractSignedAfterAug2025'] || 0
                    }`}
                    name="SOPQuestionAnswers.isMainContractSignedAfterAug2025"
                    options={[
                      { value: true, label: 'Yes' },
                      { value: false, label: 'No' },
                    ]}
                    isDisabled={adminMode ? false : !questionStates.Q1.enabled || isOfferInBookingStatus}
                    isRow
                  />
                </Box>
              )}

              {questionStates.Q2.visible && (
                <Box pb={3} borderBottom="1px solid" borderColor="gray.100">
                  <Text fontWeight="semibold" mb={3} fontSize="md">
                    Q2: Is main contract is for construction work with contract values &gt; HKD 5m or is main contract
                    is for related goods/ services with contract values &gt; HKD 500k?
                  </Text>
                  <Radio
                    key={`SOPQuestionAnswers.isOver5Million-${
                      fieldResetKeys['SOPQuestionAnswers.isOver5Million'] || 0
                    }`}
                    name="SOPQuestionAnswers.isOver5Million"
                    options={[
                      { value: true, label: 'Yes' },
                      { value: false, label: 'No' },
                    ]}
                    isDisabled={adminMode ? false : !questionStates.Q2.enabled || isOfferInBookingStatus}
                    isRow
                  />
                </Box>
              )}

              {questionStates.Q3.visible && (
                <Box pb={3} borderBottom="1px solid" borderColor="gray.100">
                  <Text fontWeight="semibold" mb={3} fontSize="md">
                    Q3: Is public contract (i.e. main contract party is government or a body specified in Schedule 3 of
                    the Ordinance)?
                    <br />
                    <Link
                      href="https://www.elegislation.gov.hk/hk/cap652!en?INDEX_CS=N&xpid=ID_1735618935869_730"
                      isExternal
                      color="blue.500"
                      textDecoration="underline"
                    >
                      Government or a body specified in Schedule 3 of the Ordinance
                    </Link>
                  </Text>
                  <Radio
                    key={`SOPQuestionAnswers.isPublicContract-${
                      fieldResetKeys['SOPQuestionAnswers.isPublicContract'] || 0
                    }`}
                    name="SOPQuestionAnswers.isPublicContract"
                    options={[
                      { value: true, label: 'Yes' },
                      { value: false, label: 'No' },
                    ]}
                    isDisabled={adminMode ? false : !questionStates.Q3.enabled || isOfferInBookingStatus}
                    isRow
                  />
                </Box>
              )}

              {questionStates.Q4.visible && (
                <Box pb={3} borderBottom="1px solid" borderColor="gray.100">
                  <Text fontWeight="semibold" mb={3} fontSize="md">
                    Q4: Is new building project?
                  </Text>
                  <Radio
                    key={`SOPQuestionAnswers.isNewBuildingProject-${
                      fieldResetKeys['SOPQuestionAnswers.isNewBuildingProject'] || 0
                    }`}
                    name="SOPQuestionAnswers.isNewBuildingProject"
                    options={[
                      { value: true, label: 'Yes' },
                      { value: false, label: 'No' },
                    ]}
                    isDisabled={adminMode ? false : !questionStates.Q4.enabled || isOfferInBookingStatus}
                    isRow
                  />
                </Box>
              )}

              {questionStates.Q5.visible && (
                <Box pb={3} borderBottom="1px solid" borderColor="gray.100">
                  <Text fontWeight="semibold" mb={3} fontSize="md">
                    Q5: Is the value of the works requiring approval of the Building Authority (BA)?
                  </Text>
                  <Radio
                    key={`SOPQuestionAnswers.isValueOfWorkRequiredBAApproval-${
                      fieldResetKeys['SOPQuestionAnswers.isValueOfWorkRequiredBAApproval'] || 0
                    }`}
                    name="SOPQuestionAnswers.isValueOfWorkRequiredBAApproval"
                    options={[
                      { value: true, label: 'Yes' },
                      { value: false, label: 'No' },
                    ]}
                    isDisabled={adminMode ? false : !questionStates.Q5.enabled || isOfferInBookingStatus}
                    isRow
                  />
                </Box>
              )}

              {questionStates.Q6.visible && (
                <Box pb={3}>
                  <Text fontWeight="semibold" mb={3} fontSize="md">
                    Q6: Is the non-residential portion contract values &gt;= HKD 5m and related goods / services
                    contracts values &gt;=HKD 500k?
                  </Text>
                  <Radio
                    key={`SOPQuestionAnswers.isNonResidentialOver5Million-${
                      fieldResetKeys['SOPQuestionAnswers.isNonResidentialOver5Million'] || 0
                    }`}
                    name="SOPQuestionAnswers.isNonResidentialOver5Million"
                    options={[
                      { value: true, label: 'Yes' },
                      { value: false, label: 'No' },
                    ]}
                    isDisabled={adminMode ? false : !questionStates.Q6.enabled || isOfferInBookingStatus}
                    isRow
                  />
                </Box>
              )}
            </VStack>
          </ModalBody>

          <ModalFooter borderTop="1px solid" borderColor="gray.200" flexShrink={0}>
            <HStack>
              {!isOfferInBookingStatus && !adminMode && (
                <Button variant="ghost" onClick={handleModalClose}>
                  Cancel
                </Button>
              )}
              {!isOfferInBookingStatus && !adminMode && (
                <Button colorScheme="blue" type="submit">
                  Save
                </Button>
              )}
            </HStack>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  )
}

export default SOPQuestionnaireModal
